import { useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function ForcePasswordChange({ onDone }) {
  const { setUser, logout } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      toast.error("As senhas não coincidem");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      const { data } = await api.get("/auth/me");
      setUser(data);
      toast.success("Nova senha definida com sucesso");
      if (onDone) onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-zinc-950 flex items-center justify-center p-6" data-testid="force-password-screen">
      <div className="w-full max-w-sm bg-zinc-900 border border-zinc-800 rounded-lg p-6">
        <div className="h-10 w-10 rounded-md bg-indigo-600/15 flex items-center justify-center mb-4">
          <KeyRound className="h-5 w-5 text-indigo-400" />
        </div>
        <h1 className="text-lg font-semibold text-zinc-100" data-testid="force-password-title">Altere sua senha</h1>
        <p className="text-sm text-zinc-400 mt-1 mb-5">
          Sua senha foi redefinida pela administração. Entre com a senha provisória e escolha uma nova senha pessoal para continuar.
        </p>
        <form onSubmit={submit} className="space-y-4" data-testid="force-password-form">
          <div>
            <Label className="text-xs text-zinc-400">Senha provisória</Label>
            <Input data-testid="force-current-password" type="password" required value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Nova senha (mín. 6 caracteres)</Label>
            <Input data-testid="force-new-password" type="password" required minLength={6} value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Confirmar nova senha</Label>
            <Input data-testid="force-confirm-password" type="password" required minLength={6} value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
          </div>
          <Button data-testid="force-password-submit" type="submit" disabled={saving}
            className="w-full bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar nova senha"}
          </Button>
        </form>
        <button type="button" data-testid="force-password-logout" onClick={logout}
          className="w-full text-center text-xs text-zinc-500 hover:text-zinc-300 mt-4 transition-colors duration-200">
          Sair e voltar ao login
        </button>
      </div>
    </div>
  );
}
